import * as React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import { useIntl } from 'react-intl';
import { useQueries } from '@tanstack/react-query';
import {
  GRAPH_LIMIT,
  IGraphData,
  IGraphResponseData,
  getIsMoreThanOneDay,
  getPointsData,
  getPointsCount,
  getVehicleData,
  hadleGraphDataAccordingToLimit,
} from '../GraphService';
import EvBatteryChart from './EvBatteryChart';
import RefreshButton from '../RefreshButton';
import { graphCurrentPointsDataAtom, IGraphCurrentPointsData } from '../GraphState';
import { onlinePanelForVehicleSelector } from '../../OnlinePanel/OnlinePanelState';

interface IProps {
  vehicleId: number;
  responseData: IGraphResponseData[];
  isCompressed: boolean;
  isSelectable: boolean;
  selectedDate: Date[];
  vehicleTankCapacity: number;
  isDetail: boolean;
  reloadData: () => void;
}

function Driving(props: IProps) {
  const { formatMessage: f } = useIntl();
  const [pointsCount, setPointsCount] = React.useState(0);
  const [graphData, setGraphData] = React.useState<IGraphData>({
    xLabels: [],
    yLabels: [],
    tooltips: {},
    minLabels: [],
    maxLabels: [],
  });
  const setGraphCurrentPointsData = useSetRecoilState<IGraphCurrentPointsData[] | null>(
    graphCurrentPointsDataAtom
  );
  const [isReloadIcon, setIsReloadIcon] = React.useState<null | Date>(null);
  const [ignitionOnFirstTs, setIgnitionOnFirstTs] = React.useState<string | null>('');
  const onlinePanel = useRecoilValue(onlinePanelForVehicleSelector(Number(props.vehicleId)));

  const getData = () => {
    const xLabels: string[] = [];
    const yLabels: (number | null)[] = [];
    const minLabels: string[] = [];
    const maxLabels: string[] = [];
    const tooltips = {};
    if (props.responseData) {
      setPointsCount(getPointsCount(props.responseData, ignitionOnFirstTs));
      props.responseData.forEach((i) => {
        if (!xLabels.includes(i.timestamp)) {
          xLabels.push(i.timestamp);
          yLabels.push(i.value);
          minLabels.push(i.min);
          maxLabels.push(i.max);
          tooltips[i.timestamp] = {
            address: i.address || `${i.latitude}, ${i.longitude}`,
            code: i.code,
          };
        }
      });
    }
    setGraphData({
      xLabels,
      yLabels,
      minLabels,
      maxLabels,
      tooltips,
    });
  };

  React.useEffect(() => {
    if (props.responseData && ignitionOnFirstTs !== '') {
      getData();
      // setIsRideJustEnded(false);
      setIsReloadIcon(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.responseData, ignitionOnFirstTs]);

  const handleVehicleData = async () => {
    const res = await getVehicleData(props.vehicleId);
    if (res !== false) {
      setIgnitionOnFirstTs(res);
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['graphVehicleData', props.vehicleId],
        queryFn: () => handleVehicleData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  React.useEffect(() => {
    if (
      props.vehicleId &&
      onlinePanel &&
      props.selectedDate &&
      // previousTimestamp &&
      !graphData.xLabels.includes(onlinePanel.timestamp) &&
      new Date(graphData.xLabels[graphData.xLabels.length - 1]) < new Date(onlinePanel.timestamp) &&
      // String(onlinePanel.timestamp) !== previousTimestamp &&
      new Date(onlinePanel.timestamp) <= props.selectedDate[1] &&
      new Date(onlinePanel.timestamp) >= props.selectedDate[0]
    ) {
      if (onlinePanel.fuelLevelLiters !== null) {
        if (!props.isCompressed) {
          let tempGraphData = { ...graphData };
          // if (
          //   new Date(graphData.xLabels[graphData.xLabels.length - 1]).getTime() >
          //   new Date(onlinePanel.timestamp).getTime()
          // ) {
          //   const timestamps: string[] = [];
          //   const yData: (number | null)[] = [];
          //   const tooltipsData = {};
          //   const labels = graphData.xLabels;
          //   let key = 0;

          //   while (new Date(labels[key]).getTime() < new Date(onlinePanel.timestamp).getTime()) {
          //     if (labels.length > GRAPH_LIMIT) {
          //       if (key !== 0) {
          //         timestamps.push(labels[key]);
          //         yData.push(graphData.yLabels[key]);
          //         tooltipsData[key - 1] = graphData.tooltips[key];
          //       }
          //       key += 1;
          //     } else {
          //       timestamps.push(labels[key]);
          //       yData.push(graphData.yLabels[key]);
          //       tooltipsData[key] = graphData.tooltips[key];
          //       key += 1;
          //     }
          //   }
          //   timestamps.push(onlinePanel.timestamp);
          //   yData.push(onlinePanel.fuelLevelPercentage);
          //   tooltipsData[key] = {
          //     address: onlinePanel.address || `${onlinePanel.latitude}, ${onlinePanel.longitude}`,
          //   };
          //   labels.forEach((item: string, oldKey: number) => {
          //     if (oldKey >= key) {
          //       timestamps.push(item);
          //       yData.push(graphData.yLabels[oldKey]);
          //       tooltipsData[oldKey + 1] = graphData.tooltips[oldKey];
          //     }
          //   });
          //   setGraphData({
          //     xLabels: getLimitedValues(timestamps),
          //     yLabels: getLimitedNumbers(yData),
          //     minLabels: getLimitedValues([...graphData.minLabels]),
          //     maxLabels: getLimitedValues([...graphData.maxLabels]),
          //     tooltips: getLimitedObjectValues(tooltipsData),
          //   });
          // } else {
          tempGraphData.xLabels.push(onlinePanel.timestamp);
          tempGraphData.yLabels.push(onlinePanel.fuelLevelLiters);
          tempGraphData.tooltips[onlinePanel.timestamp] = {
            address: onlinePanel.address || `${onlinePanel.latitude}, ${onlinePanel.longitude}`,
            code: onlinePanel.code,
          };

          tempGraphData = hadleGraphDataAccordingToLimit(tempGraphData, pointsCount + 1);

          setPointsCount(pointsCount + 1);
          setGraphData(tempGraphData);
          // }
        }
        setIsReloadIcon(new Date());
      }
    }
  }, [
    graphData,
    onlinePanel,
    pointsCount,
    props.isCompressed,
    props.selectedDate,
    props.vehicleId,
  ]);

  React.useEffect(() => {
    if (graphData?.xLabels && graphData.xLabels.length > 0) {
      const pointData = getPointsData(graphData);
      setGraphCurrentPointsData(pointData);
    }
  }, [graphData, setGraphCurrentPointsData]);

  return (
    <div>
      {!props.isCompressed && pointsCount > GRAPH_LIMIT && (
        <div style={{ padding: props.isDetail ? '20px 0 0 0' : '' }}>
          <CmdAlert
            id="velocity-graph-alert"
            data={{
              title: f({ id: 'graph.velocity.limitAlert.title' }),
              message: props.isDetail
                ? f({ id: 'graph.graphLimitInRange.alert.message' })
                : f({ id: 'graph.onlinepanel.alert.message' }),
              type: 'info',
            }}
            noCancel
          />
        </div>
      )}
      <RefreshButton
        loadData={props.reloadData}
        isActive={isReloadIcon}
        selectedDate={props.selectedDate}
        isCompressed={props.isCompressed}
        pointsCount={pointsCount}
        isDetail={props.isDetail}
      />
      <EvBatteryChart
        graphData={graphData}
        noAnimation="animation"
        isCompressed={props.isCompressed}
        isOnline
        isSelectable={props.isSelectable}
        vehicleTankCapacity={props.vehicleTankCapacity}
        isMoreThanOneDay={getIsMoreThanOneDay(props.selectedDate)}
      />
    </div>
  );
}

export default React.memo(Driving);
